import React, { useState } from "react";
import { FaCalculator, FaMoneyBillWave, FaClock, FaPercent, FaChartPie, FaRupeeSign } from "react-icons/fa";

export default function FDCalculator() {
    const [investment, setInvestment] = useState("");
    const [durationValue, setDurationValue] = useState("12");
    const [unit, setUnit] = useState("mo"); // 'yr', 'mo', 'da'
    const [roi, setRoi] = useState("9.5");

    const principal = parseFloat(investment) || 0;
    const rate = parseFloat(roi) || 0;
    const val = parseFloat(durationValue) || 0;

    // Unit-based Time Calculation
    let timeInYears = 0;
    if (unit === 'yr') timeInYears = val;
    else if (unit === 'mo') timeInYears = val / 12;
    else if (unit === 'da') timeInYears = val / 365;

    // Fixed to Simple Interest calculation
    const maturityAmount = (principal && rate && timeInYears > 0)
        ? principal * (1 + (rate * timeInYears) / 100)
        : 0;

    const totalInterest = maturityAmount - principal;

    const inputClasses = "w-full pl-10 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all text-lg font-medium text-gray-800 placeholder-gray-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";
    const simpleInputClasses = "w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all text-lg font-medium text-gray-800 placeholder-gray-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 py-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center">
            {/* Header Section */}
            <div className="text-center mb-10 transform hover:scale-105 transition-transform duration-300">
                <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">
                    FD Return Calculator
                </h1>
                <p className="text-gray-600 text-lg">Calculate your Fixed Deposit returns</p>
            </div>

            <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                {/* Input Section */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-blue-100 hover:shadow-2xl transition-shadow duration-300">
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-500 p-6 text-white">
                        <h2 className="text-2xl font-bold flex items-center">
                            <FaCalculator className="mr-3" /> FD Details
                        </h2>
                    </div>

                    <div className="p-8 space-y-6">
                        {/* Investment Input */}
                        <div className="group">
                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                <FaMoneyBillWave className="mr-2 text-blue-500" /> Investment Amount (₹)
                            </label>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaRupeeSign className="text-gray-400" />
                                </span>
                                <input
                                    type="number"
                                    value={investment}
                                    onChange={(e) => setInvestment(e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder="Enter Investment Amount"
                                    className={inputClasses}
                                />
                            </div>
                        </div>

                        {/* Period Input */}
                        <div className="group">
                            <label className="block text-sm font-semibold text-gray-700 mb-4 flex items-center">
                                <FaClock className="mr-2 text-blue-500" /> Time Period
                            </label>

                            {/* Unit Selector Buttons */}
                            <div className="flex p-1 bg-gray-100 rounded-xl mb-4">
                                <button
                                    onClick={() => setUnit('yr')}
                                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-all ${unit === 'yr' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    Years
                                </button>
                                <button
                                    onClick={() => setUnit('mo')}
                                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-all ${unit === 'mo' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    Months
                                </button>
                                <button
                                    onClick={() => setUnit('da')}
                                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-all ${unit === 'da' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    Days
                                </button>
                            </div>

                            <div className="relative">
                                <input
                                    type="number"
                                    value={durationValue}
                                    onChange={(e) => setDurationValue(e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder={`Enter ${unit === 'yr' ? 'Years' : unit === 'mo' ? 'Months' : 'Days'}`}
                                    className={simpleInputClasses}
                                />
                                <span className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-500 font-medium">
                                    {unit === 'yr' ? 'Years' : unit === 'mo' ? 'Months' : 'Days'}
                                </span>
                            </div>
                        </div>

                        {/* Interest Input */}
                        <div className="group">
                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                <FaPercent className="mr-2 text-blue-500" /> Interest Rate (% Annual)
                            </label>
                            <input
                                type="number"
                                value={roi}
                                onChange={(e) => setRoi(e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                placeholder="Interest Rate"
                                className={simpleInputClasses}
                            />
                        </div>
                    </div>
                </div>

                {/* Results Section */}
                {principal > 0 ? (
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-blue-100 hover:shadow-2xl transition-shadow duration-300 h-full flex flex-col">
                        <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-6 text-white">
                            <h2 className="text-2xl font-bold flex items-center">
                                <FaChartPie className="mr-3 text-blue-400" /> Returns Summary
                            </h2>
                        </div>

                        <div className="p-8 flex-1 flex flex-col justify-center space-y-6">

                            {/* Maturity Amount Card */}
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100 text-center transform hover:scale-105 transition-transform duration-300">
                                <p className="text-gray-600 font-semibold mb-2">Maturity Amount</p>
                                <p className="text-4xl px-2 font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                                    ₹{maturityAmount ? maturityAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : "0.00"}
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-white hover:shadow-md transition-all text-center">
                                    <p className="text-sm text-gray-500 mb-1">Invested Amount</p>
                                    <p className="text-xl font-bold text-gray-800">
                                        ₹{principal ? principal.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : "0.00"}
                                    </p>
                                </div>
                                <div className="p-4 rounded-xl border border-green-100 bg-green-50 hover:bg-white hover:shadow-md transition-all text-center">
                                    <p className="text-sm text-green-600 mb-1">Total Returns</p>
                                    <p className="text-xl font-bold text-green-700">
                                        ₹{totalInterest ? totalInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : "0.00"}
                                    </p>
                                </div>
                            </div>

                            {/* Progress Bar Visual */}
                            <div className="mt-4 pt-6 border-t border-gray-100">
                                <div className="flex justify-between text-sm font-medium text-gray-600 mb-2">
                                    <span>Investment</span>
                                    <span>Returns</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden flex">
                                    <div
                                        className="bg-gray-800 h-full"
                                        style={{ width: `${(principal / maturityAmount) * 100}%` }}
                                    ></div>
                                    <div
                                        className="bg-blue-500 h-full"
                                        style={{ width: `${(totalInterest / maturityAmount) * 100}%` }}
                                    ></div>
                                </div>
                                <div className="flex justify-between text-xs text-gray-500 mt-2">
                                    <span>{((principal / maturityAmount) * 100).toFixed(1)}%</span>
                                    <span>{((totalInterest / maturityAmount) * 100).toFixed(1)}%</span>
                                </div>
                            </div>

                        </div>
                    </div>
                ) : (
                    <div className="hidden lg:flex h-full items-center justify-center bg-white/50 rounded-2xl border-2 border-dashed border-gray-200 text-gray-400 p-8">
                        <div className="text-center">
                            <FaCalculator className="text-6xl mx-auto mb-4 text-blue-200" />
                            <p className="text-xl font-medium">Enter investment details to check returns</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
